import React, { useState } from 'react';
import { TAItem, TAIndicatorDef } from '../../types';
import { TA_CATALOGUE, MAX_TA, TA_COLORS } from '../../constants';
import {
  getDefaultLines,
  getDefaultPeriod,
  getDisplayPeriod,
  INDICATOR_CONFIGS,
} from '../../indicatorConfigs';
import IconButton from '@mui/material/IconButton';
import { ReactComponent as CloseCircleIcon } from '../../../../assets/icons/close-circle-fill.svg';
import { ReactComponent as ResetIcon } from '../../../../assets/icons/StockChart/replay_24px.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import SettingsPopup from '../common/SettingsPopup';
import IndicatorSettings from './IndicatorSettings';
import AvailableIndicatorsList from './AvailableIndicatorsList';
import '../../../../Components/Common/typography.css';
import './TechnicalAnalysisSettings.css';

interface TechnicalAnalysisSettingsProps {
  activeTA: TAItem[];
  onAdd: (item: TAItem) => void;
  onRemove: (key: string) => void;
  onUpdate: (key: string, changes: Partial<TAItem>) => void;
}

const TechnicalAnalysisSettings: React.FC<TechnicalAnalysisSettingsProps> = ({
  activeTA,
  onAdd,
  onRemove,
  onUpdate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTA, setExpandedTA] = useState<Record<string, boolean>>({});

  const allActiveKeys = activeTA.map((t) => t.key);
  const atMax = activeTA.length >= MAX_TA;

  // Filter available indicators (exclude already selected)
  const filteredCatalogue = Object.entries(TA_CATALOGUE).reduce<
    Record<string, TAIndicatorDef[]>
  >((acc, [cat, items]) => {
    const available = items.filter((i) => !allActiveKeys.includes(i.key));
    const filtered = searchQuery.trim()
      ? available.filter((i) =>
          i.label.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : available;
    if (filtered.length) acc[cat] = filtered;
    return acc;
  }, {});

  const handleAdd = (indicator: TAIndicatorDef, category: string) => {
    if (atMax) return;
    const lines = getDefaultLines(indicator.key);
    const config = INDICATOR_CONFIGS[indicator.key];
    // Use first color from indicator config, fallback to TA_COLORS rotation
    const firstConfigColor = config?.settings.find((s) => s.color)?.color;
    const fallbackColor = TA_COLORS[activeTA.length % TA_COLORS.length];
    const newItem: TAItem = {
      key: indicator.key,
      label: indicator.label,
      category,
      color: firstConfigColor ?? fallbackColor,
      period: getDefaultPeriod(indicator.key),
      ...(lines ? { lines } : {}),
    };
    onAdd(newItem);
    setExpandedTA((prev) => {
      const allClosed: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => {
        allClosed[k] = false;
      });
      return { ...allClosed, [indicator.key]: true };
    });
  };

  const toggleExpand = (key: string) => {
    setExpandedTA((prev) => {
      if (prev[key]) {
        return { ...prev, [key]: false };
      }
      const allClosed: Record<string, boolean> = {};
      Object.keys(prev).forEach((k) => {
        allClosed[k] = false;
      });
      return { ...allClosed, [key]: true };
    });
  };

  const handleResetItem = (ta: TAItem) => {
    const config = INDICATOR_CONFIGS[ta.key];
    const firstConfigColor = config?.settings.find((s) => s.color)?.color;
    const fallbackColor =
      TA_COLORS[activeTA.findIndex((t) => t.key === ta.key) % TA_COLORS.length];
    const lines = getDefaultLines(ta.key);
    onUpdate(ta.key, {
      color: firstConfigColor ?? fallbackColor,
      period: getDefaultPeriod(ta.key),
      paneHeight: 1,
      selectValues: undefined,
      ...(lines ? { lines } : {}),
    });
  };

  return (
    <SettingsPopup width={340} className="ta-settings-popup">
      {/* Selected Indicators Section */}
      {activeTA.length > 0 && (
        <div className="ta-selected-section">
          <div className="ta-settings-section-title body-s-regular">
            Selected Technical Analysis
          </div>

          {activeTA.map((ta) => {
            const isExpanded = expandedTA[ta.key] ?? false;
            const displayPeriod = getDisplayPeriod(ta);
            return (
              <div key={ta.key} className="ta-selected-block">
                <div
                  className="ta-selected-row"
                  onClick={() => toggleExpand(ta.key)}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(ta.key);
                    }}
                    aria-label={`Remove ${ta.label}`}
                    sx={{
                      padding: 0,
                      color: 'var(--color-brand)',
                      '& svg path': {
                        fill: 'var(--color-brand)',
                      },
                    }}
                  >
                    <CloseCircleIcon />
                  </IconButton>
                  <span
                    className="body-m-regular ta-selected-name"
                    style={{ fontWeight: isExpanded ? 700 : 400 }}
                  >
                    {ta.label}
                    {!isExpanded && displayPeriod && (
                      <span className="body-s-regular ta-selected-period">
                        ({displayPeriod})
                      </span>
                    )}
                  </span>
                  <span className="ta-selected-actions">
                    {isExpanded && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetItem(ta);
                        }}
                        aria-label={`Reset ${ta.label}`}
                        sx={{
                          padding: '2px',
                          '& svg path': { fill: 'var(--accent-primary)' },
                        }}
                      >
                        <ResetIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(ta.key);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={`Toggle ${ta.label} settings`}
                      sx={{ padding: '2px' }}
                      className={isExpanded ? 'ta-collapse-toggle-up' : ''}
                    >
                      <ArrowDownIcon />
                    </IconButton>
                  </span>
                </div>

                {isExpanded && (
                  <IndicatorSettings ta={ta} onUpdate={onUpdate} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Available Indicators Section */}
      <AvailableIndicatorsList
        filteredCatalogue={filteredCatalogue}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAdd={handleAdd}
        atMax={atMax}
      />
    </SettingsPopup>
  );
};

export default TechnicalAnalysisSettings;
